/**
 * Die Strich-Icons der Seitenleiste — eigene Pfade auf einem 24er-Raster, 1,75 Strich, runde Enden.
 * Inline statt als Paket: es sind sechzehn Formen, und jede steht neben einem Wort, das sie benennt.
 * Deshalb sind alle `aria-hidden` — der Name eines Links ist sein Wort, nicht sein Bild.
 */
import type { ReactElement } from 'react'

export type IconName =
  | 'dashboard'
  | 'pruefen'
  | 'abschluesse'
  | 'provisionen'
  | 'rechnungen'
  | 'zahlungen'
  | 'regeln'
  | 'exporte'
  | 'einstellungen'
  | 'geldboerse'
  | 'pipeline'
  | 'abrechnen'
  | 'profil'
  | 'mandanten'
  | 'system'
  | 'plattform'
  | 'abmelden'
  | 'ort'

const PFADE: Record<IconName, ReactElement> = {
  dashboard: (
    <>
      <rect x="3.5" y="3.5" width="7" height="8.5" rx="1.75" />
      <rect x="13.5" y="3.5" width="7" height="5" rx="1.75" />
      <rect x="13.5" y="11.5" width="7" height="9" rx="1.75" />
      <rect x="3.5" y="15" width="7" height="5.5" rx="1.75" />
    </>
  ),
  pruefen: (
    <>
      <circle cx="12" cy="12" r="8.5" />
      <path d="m8.5 12.25 2.4 2.4 4.6-4.9" />
    </>
  ),
  abschluesse: (
    <>
      <path d="M14 3.5H7.5a2 2 0 0 0-2 2v13a2 2 0 0 0 2 2h9a2 2 0 0 0 2-2V8z" />
      <path d="M14 3.5V8h4.5" />
      <path d="M8.75 16.25c1-1.4 1.9-1.4 2.4-.1.5 1.2 1.4 1.1 2.35-.3" />
    </>
  ),
  provisionen: (
    <>
      <circle cx="7.5" cy="7.5" r="2.5" />
      <circle cx="16.5" cy="16.5" r="2.5" />
      <path d="M18.5 5.5 5.5 18.5" />
    </>
  ),
  rechnungen: (
    <>
      <path d="M6 3.5h12v17l-3-1.75-3 1.75-3-1.75-3 1.75z" />
      <path d="M9 8.5h6M9 12h6M9 15.5h3.5" />
    </>
  ),
  zahlungen: (
    <>
      <rect x="2.75" y="5.5" width="18.5" height="13" rx="2.5" />
      <path d="M2.75 10h18.5M6.5 14.75h3.5" />
    </>
  ),
  regeln: (
    <>
      <path d="M4 7.5h9.5M17.5 7.5H20M4 16.5h2.5M10.5 16.5H20" />
      <circle cx="15.5" cy="7.5" r="2" />
      <circle cx="8.5" cy="16.5" r="2" />
    </>
  ),
  exporte: (
    <>
      <path d="M12 4v10.5M7.75 10.5 12 14.75l4.25-4.25" />
      <path d="M4.5 16.5v1.5a2 2 0 0 0 2 2h11a2 2 0 0 0 2-2v-1.5" />
    </>
  ),
  einstellungen: (
    <>
      <path d="M12 3 19.8 7.5v9L12 21l-7.8-4.5v-9z" />
      <circle cx="12" cy="12" r="2.75" />
    </>
  ),
  geldboerse: (
    <>
      <path d="M18.5 7.5V6a1.5 1.5 0 0 0-1.5-1.5H6A2.5 2.5 0 0 0 3.5 7v10.5A2 2 0 0 0 5.5 19.5h13a2 2 0 0 0 2-2v-8a2 2 0 0 0-2-2H6" />
      <path d="M16.25 13.5h.01" />
    </>
  ),
  pipeline: (
    <>
      <path d="m3.5 16.5 5.5-5.5 4 4 7.5-7.5" />
      <path d="M15 7.5h5.5V13" />
    </>
  ),
  abrechnen: (
    <>
      <path d="M14 3.5H7.5a2 2 0 0 0-2 2v13a2 2 0 0 0 2 2h9a2 2 0 0 0 2-2V8z" />
      <path d="M14 3.5V8h4.5" />
      <path d="M12 11v6M9 14h6" />
    </>
  ),
  profil: (
    <>
      <circle cx="12" cy="8.25" r="3.75" />
      <path d="M4.75 20c.6-3.6 3.6-6 7.25-6s6.65 2.4 7.25 6" />
    </>
  ),
  mandanten: (
    <>
      <rect x="4.5" y="3.5" width="15" height="17" rx="2" />
      <path d="M9 7.5h1.5M13.5 7.5H15M9 11h1.5M13.5 11H15M9 14.5h1.5M13.5 14.5H15M10.5 20.5v-2.75h3v2.75" />
    </>
  ),
  system: <path d="M3 12h3.75l2.75-7 5 14 2.75-7H21" />,
  plattform: <path d="M12 3.25 19.5 6v5.75c0 4.3-3.2 8-7.5 9-4.3-1-7.5-4.7-7.5-9V6z" />,
  abmelden: (
    <>
      <path d="M14.5 4.5h3a2 2 0 0 1 2 2v11a2 2 0 0 1-2 2h-3" />
      <path d="M9.75 16.25 5.5 12l4.25-4.25M5.5 12h10" />
    </>
  ),
  ort: (
    <>
      <path d="M12 20.75s-6.5-5.6-6.5-10.5a6.5 6.5 0 0 1 13 0c0 4.9-6.5 10.5-6.5 10.5z" />
      <circle cx="12" cy="10.25" r="2.25" />
    </>
  ),
}

export function Icon({ name, className }: { name: IconName; className?: string }): ReactElement {
  return (
    <svg
      className={className ?? 'icon'}
      viewBox="0 0 24 24"
      width="20"
      height="20"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.75"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
    >
      {PFADE[name]}
    </svg>
  )
}
